import { useMemo } from "react";
import { Sun, Zap, Battery, Home, Cpu } from "lucide-react";

/**
 * Live energy-flow diagram for a plant.
 *
 * Design notes
 * ------------
 * * White card that stands out from the dark SCADA dashboard around it.
 * * SVG uses a 100×100 viewBox with `preserveAspectRatio="none"` so the
 *   numeric path coordinates map cleanly to responsive percentages of the
 *   node grid.
 * * Direction is shown by an animated dash marching along each flow line
 *   plus an arrowhead at the destination end. On-grid plants render the
 *   Battery lane collapsed.
 */

const COLORS = {
    pv: "#0A0A0A",       // orange
    grid: "#16A34A",     // green
    battery: "#3B82F6",  // blue
    load: "#111827",     // near-black
    inverter: "#0A0A0A",
    idle: "#D4D4D8",
};

// Node coordinates in the 100×100 viewBox
const NODES = {
    pv:       { x: 12, y: 18, side: "left",  vSide: "top" },
    grid:     { x: 88, y: 18, side: "right", vSide: "top" },
    inverter: { x: 50, y: 50, side: null,    vSide: null },
    battery:  { x: 12, y: 82, side: "left",  vSide: "bottom" },
    load:     { x: 88, y: 82, side: "right", vSide: "bottom" },
};

function Node({ icon: Icon, color, label, value, unit, sub, testid }) {
    return (
        <div className="flex flex-col items-center pointer-events-auto" data-testid={testid}>
            <div
                className="w-16 h-16 rounded-full flex items-center justify-center border-2 bg-white shadow-sm"
                style={{ borderColor: color, boxShadow: `0 0 0 6px ${color}12` }}
            >
                <Icon className="w-7 h-7" style={{ color }} strokeWidth={1.6} />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{label}</div>
            <div className="font-mono text-sm font-medium text-zinc-900">
                {value ?? "—"}
                {unit && <span className="text-[10px] text-zinc-500 ml-0.5">{unit}</span>}
            </div>
            {sub && <div className="text-[10px] mt-0.5 text-zinc-500">{sub}</div>}
        </div>
    );
}

function fmtKW(v) {
    if (v == null || Number.isNaN(v)) return null;
    const abs = Math.abs(v);
    return abs >= 1 ? abs.toFixed(2) : (abs * 1000).toFixed(0);
}
function fmtUnit(v) {
    if (v == null || Number.isNaN(v)) return "";
    return Math.abs(v) >= 1 ? "kW" : "W";
}

// Build a smooth SVG cubic-bezier from node A to node B in viewBox coords.
function bezier(from, to) {
    const midY = (from.y + to.y) / 2;
    return `M ${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`;
}

/**
 * FlowLine — an animated dashed path plus arrowhead.
 * `direction === "forward"` = A → B (dashes march A → B).
 * `direction === "reverse"` = B → A (dashes march B → A).
 * `direction === null`      = idle (no animation, muted color).
 */
function FlowLine({ from, to, color, direction, testid }) {
    const active = direction === "forward" || direction === "reverse";
    const stroke = active ? color : COLORS.idle;
    const forward = direction !== "reverse";
    const d = bezier(from, to);
    // Use two overlapping paths — a soft glow beneath and a marching-dash on top.
    return (
        <g data-testid={testid}>
            <path d={d} stroke={stroke} strokeWidth={0.5} fill="none" opacity={active ? 0.35 : 0.6} />
            {active && (
                <path
                    d={forward ? d : bezier(to, from)}
                    stroke={color}
                    strokeWidth={1.1}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="3 3"
                    style={{ animation: "flowMarch 1.1s linear infinite" }}
                    markerEnd={`url(#arrow-${color.replace("#", "")})`}
                />
            )}
        </g>
    );
}

export default function EnergyFlowGraph({ data }) {
    const isHybrid = data?.system_type === "hybrid";
    const flow = data?.flow || {};

    const pv = flow.pv_kw ?? null;
    const grid = flow.grid_kw ?? null;
    const gridDir = flow.grid_direction;
    const load = flow.load_kw ?? null;
    const batt = flow.battery_kw ?? null;
    const battDir = flow.battery_direction;
    const soc = flow.battery_soc ?? null;

    // Direction map for each edge — encodes which way the arrow points.
    const edges = useMemo(() => {
        const nz = (v, t = 0.005) => (v ?? 0) > t;
        return {
            // PV always flows toward inverter when producing.
            pv: nz(pv) ? "forward" : null,
            // Grid: import = grid → inverter, export = inverter → grid.
            grid: nz(grid)
                ? (gridDir === "export" ? "reverse" : "forward")
                : null,
            // Battery: charging = inverter → battery (reverse), discharging = battery → inverter (forward).
            battery: isHybrid && nz(batt)
                ? (battDir === "charging" ? "reverse" : "forward")
                : null,
            // Load always draws from inverter.
            load: nz(load) ? "forward" : null,
        };
    }, [pv, grid, gridDir, batt, battDir, isHybrid, load]);

    // Unique arrowhead colors for use in <defs>
    const arrowColors = [COLORS.pv, COLORS.grid, COLORS.battery, COLORS.load];

    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 shadow-sm" data-testid="energy-flow-graph">
            <style>{`
                @keyframes flowMarch { to { stroke-dashoffset: -12; } }
            `}</style>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Live Energy Flow</div>
                    <div className="mt-0.5 flex items-center gap-2">
                        <span className="text-lg font-semibold text-zinc-900">
                            {isHybrid ? "Hybrid System" : "On-Grid System"}
                        </span>
                        <span
                            className="text-[10px] font-mono px-2 py-0.5 rounded-sm border"
                            style={{
                                background: (isHybrid ? COLORS.battery : COLORS.grid) + "12",
                                color: isHybrid ? COLORS.battery : COLORS.grid,
                                borderColor: (isHybrid ? COLORS.battery : COLORS.grid) + "55",
                            }}
                            data-testid="system-type-badge"
                        >
                            {data?.grid_interconnection_type || (isHybrid ? "HYBRID" : "ON-GRID")}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-mono text-green-600" data-testid="flow-live-badge">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    LIVE
                </div>
            </div>

            {/* Diagram — nodes on a % grid + SVG overlay with matching viewBox */}
            <div className="relative w-full" style={{ height: isHybrid ? 320 : 240, minHeight: 240 }}>
                {/* SVG lines first so the nodes render on top */}
                <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                >
                    <defs>
                        {arrowColors.map((c) => (
                            <marker
                                key={c}
                                id={`arrow-${c.replace("#", "")}`}
                                viewBox="0 0 10 10"
                                refX="7"
                                refY="5"
                                markerWidth="4"
                                markerHeight="4"
                                orient="auto-start-reverse"
                                markerUnits="strokeWidth"
                            >
                                <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                            </marker>
                        ))}
                    </defs>

                    <FlowLine from={NODES.pv} to={NODES.inverter} color={COLORS.pv} direction={edges.pv} testid="flow-line-pv" />
                    <FlowLine from={NODES.inverter} to={NODES.grid} color={COLORS.grid} direction={edges.grid} testid="flow-line-grid" />
                    <FlowLine from={NODES.inverter} to={NODES.load} color={COLORS.load} direction={edges.load} testid="flow-line-load" />
                    {isHybrid && (
                        <FlowLine from={NODES.inverter} to={NODES.battery} color={COLORS.battery} direction={edges.battery} testid="flow-line-battery" />
                    )}
                </svg>

                {/* Nodes positioned as % of the diagram box */}
                <div className="absolute" style={nodePos(NODES.pv)}>
                    <Node icon={Sun} color={COLORS.pv} label="PV / Solar" value={fmtKW(pv)} unit={fmtUnit(pv)} testid="flow-node-pv" />
                </div>
                <div className="absolute" style={nodePos(NODES.grid)}>
                    <Node
                        icon={Zap}
                        color={COLORS.grid}
                        label="Grid"
                        value={fmtKW(grid)}
                        unit={fmtUnit(grid)}
                        sub={gridDir === "export" ? "Exporting" : gridDir === "import" ? "Importing" : "Idle"}
                        testid="flow-node-grid"
                    />
                </div>
                <div className="absolute" style={nodePos(NODES.inverter)}>
                    <Node
                        icon={Cpu}
                        color={COLORS.inverter}
                        label="Inverter"
                        value=""
                        unit=""
                        sub={isHybrid ? "Hybrid · primary" : "On-Grid · primary"}
                        testid="flow-node-inverter"
                    />
                </div>
                <div className="absolute" style={nodePos(NODES.load)}>
                    <Node icon={Home} color={COLORS.load} label="Home Load" value={fmtKW(load)} unit={fmtUnit(load)} testid="flow-node-load" />
                </div>
                {isHybrid && (
                    <div className="absolute" style={nodePos(NODES.battery)}>
                        <Node
                            icon={Battery}
                            color={COLORS.battery}
                            label="Battery"
                            value={fmtKW(batt)}
                            unit={fmtUnit(batt)}
                            sub={soc != null ? `SOC ${Math.round(soc)}%` : (battDir === "charging" ? "Charging" : battDir === "discharging" ? "Discharging" : "Idle")}
                            testid="flow-node-battery"
                        />
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-wrap gap-x-6 gap-y-2 items-center">
                <LegendDot color={COLORS.pv} label="PV" />
                <LegendDot color={COLORS.grid} label="Grid" />
                {isHybrid && <LegendDot color={COLORS.battery} label="Battery" />}
                <LegendDot color={COLORS.load} label="Home Load" />
                <div className="ml-auto text-[10px] font-mono text-zinc-400">
                    Solid = idle · Dashed = flowing
                </div>
            </div>
        </div>
    );
}

// Position a node absolutely so that its center sits on the SVG (x, y).
function nodePos({ x, y }) {
    // Node width ~112px, height ~86px — we translate(-50%, -50%) via inline transform
    return { left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" };
}

function LegendDot({ color, label }) {
    return (
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-600">
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            {label}
        </div>
    );
}
